import api from './api';

export interface AppFeature {
  name: string;
  displayName: string;
  description: string;
}

export interface AppFeaturesResponse {
  entity_id: number;
  entity_type: 'user' | 'organization';
  app_features: string[];
  available_features: string[];
}

export interface OrganizationUsersFeatures {
  organization_id: number;
  organization_name: string;
  organization_features: string[];
  users: Array<{
    user_id: number;
    email: string;
    first_name: string;
    last_name: string;
    app_features: string[];
  }>;
  available_features: string[];
}

export interface BulkUpdateRequest {
  organizations?: Array<{
    org_id: number;
    app_features: string[];
  }>;
  users?: Array<{
    user_id: number;
    app_features: string[];
  }>;
}

export interface BulkUpdateResponse {
  organizations_updated: number;
  users_updated: number;
  errors: string[];
}

// Feature definitions with display names and descriptions
export const FEATURE_DEFINITIONS: Record<string, AppFeature> = {
  show_ide_credit_usage: {
    name: 'show_ide_credit_usage',
    displayName: 'IDE Credit Usage Display',
    description: 'Show detailed IDE credit consumption information'
  },
  advanced_analytics: {
    name: 'advanced_analytics',
    displayName: 'Advanced Analytics',
    description: 'Access to advanced usage analytics and reporting'
  },
  custom_integrations: {
    name: 'custom_integrations',
    displayName: 'Custom Integrations',
    description: 'Allow custom API integrations and webhooks'
  },
  priority_support: {
    name: 'priority_support',
    displayName: 'Priority Support',
    description: 'Access to priority support channels'
  },
  beta_features: {
    name: 'beta_features',
    displayName: 'Beta Features',
    description: 'Early access to experimental features'
  },
  enhanced_reporting: {
    name: 'enhanced_reporting',
    displayName: 'Enhanced Reporting',
    description: 'Advanced reporting capabilities and exports'
  },
  api_access: {
    name: 'api_access',
    displayName: 'API Access',
    description: 'Direct API access permissions'
  },
  white_label: {
    name: 'white_label',
    displayName: 'White Label',
    description: 'White-label customization options'
  },
  sso_integration: {
    name: 'sso_integration',
    displayName: 'SSO Integration',
    description: 'Single Sign-On integration capabilities'
  },
  audit_logs_export: {
    name: 'audit_logs_export',
    displayName: 'Audit Logs Export',
    description: 'Export audit logs functionality'
  }
};

export const appFeaturesApi = {
  // Get all available features
  getAvailableFeatures: async (): Promise<{ available_features: string[]; description: string }> => {
    const response = await api.get('/app-features/available-features');
    return response.data;
  },

  // Organization features
  getOrganizationFeatures: async (orgId: number): Promise<AppFeaturesResponse> => {
    const response = await api.get(`/app-features/organizations/${orgId}/features`);
    return response.data;
  },

  updateOrganizationFeatures: async (orgId: number, features: string[]): Promise<AppFeaturesResponse> => {
    const response = await api.put(`/app-features/organizations/${orgId}/features`, {
      app_features: features
    });
    return response.data;
  },

  // User features
  getUserFeatures: async (userId: number): Promise<AppFeaturesResponse> => {
    const response = await api.get(`/app-features/users/${userId}/features`);
    return response.data;
  },

  updateUserFeatures: async (userId: number, features: string[]): Promise<AppFeaturesResponse> => {
    const response = await api.put(`/app-features/users/${userId}/features`, {
      app_features: features
    });
    return response.data;
  },

  // Organization users features
  getOrganizationUsersFeatures: async (orgId: number): Promise<OrganizationUsersFeatures> => {
    const response = await api.get(`/app-features/organizations/${orgId}/users-features`);
    return response.data;
  },

  // Bulk update
  bulkUpdateFeatures: async (updates: BulkUpdateRequest): Promise<BulkUpdateResponse> => {
    const response = await api.post('/app-features/bulk-update', updates);
    return response.data;
  },

  // Helper function to apply organization features to all users
  applyOrganizationFeaturesToUsers: async (orgId: number, features: string[]): Promise<BulkUpdateResponse> => {
    // First get all users in the organization
    const orgUsersData = await appFeaturesApi.getOrganizationUsersFeatures(orgId);
    
    // Create bulk update request for all users
    const userUpdates = orgUsersData.users.map(user => ({
      user_id: user.user_id,
      app_features: Array.from(new Set([...features, ...user.app_features])) // Merge with existing user features
    }));

    // Update organization and all users
    return appFeaturesApi.bulkUpdateFeatures({
      organizations: [{ org_id: orgId, app_features: features }],
      users: userUpdates
    });
  },

  // Helper function to remove organization features from all users
  removeOrganizationFeaturesFromUsers: async (orgId: number, featuresToRemove: string[]): Promise<BulkUpdateResponse> => {
    // First get all users in the organization
    const orgUsersData = await appFeaturesApi.getOrganizationUsersFeatures(orgId);
    
    // Create bulk update request to remove features from all users
    const userUpdates = orgUsersData.users.map(user => ({
      user_id: user.user_id,
      app_features: user.app_features.filter(feature => !featuresToRemove.includes(feature))
    }));

    // Update all users (organization features are handled separately)
    return appFeaturesApi.bulkUpdateFeatures({
      users: userUpdates
    });
  }
};

export default appFeaturesApi;
